// Time and place. The palette in sky.ts runs on a fixed day (sunrise at 6, sunset at 18); this stretches that day to the
// real sunrise and sunset for the visitor's place and date, so a long summer evening and a short winter one look different.

export interface Loc { lat: number; lon: number; city: string; exact: boolean }

const tzHours = (): number => -new Date().getTimezoneOffset() / 60; // hours ahead of UTC
const tzCity = (): string => {
  try { return (Intl.DateTimeFormat().resolvedOptions().timeZone || '').split('/').pop()!.replace(/_/g, ' ') || 'your time zone'; }
  catch { return 'your time zone'; }
};

/** Standard-time offset (ignores daylight saving), hours ahead of UTC. */
const stdHours = (): number => {
  const y = new Date().getFullYear();
  return -Math.max(new Date(y, 0, 1).getTimezoneOffset(), new Date(y, 6, 1).getTimezoneOffset()) / 60;
};

/** Without permission the place is a guess: longitude from the time zone, latitude 40 degrees north. */
export const loc: Loc = { lat: 40, lon: stdHours() * 15, city: tzCity(), exact: false };

export function setLocation(lat: number, lon: number): void {
  loc.lat = lat; loc.lon = lon; loc.exact = true;
}

const rad = Math.PI / 180;

/** Sunrise and sunset in local clock hours (NOAA approximation), kept between 2.5 and 9.5 hours either side of noon. */
export function sunTimes(d = new Date()): { rise: number; set: number } {
  const start = Date.UTC(d.getFullYear(), 0, 0);
  const n = Math.floor((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - start) / 86400000);
  const B = (2 * Math.PI / 365) * (n - 81);
  const decl = 23.44 * rad * Math.sin(B);
  const eq = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B); // minutes
  const cosH = (Math.sin(-0.833 * rad) - Math.sin(loc.lat * rad) * Math.sin(decl)) / (Math.cos(loc.lat * rad) * Math.cos(decl));
  const H = Math.min(9.5, Math.max(2.5, Math.acos(Math.min(1, Math.max(-1, cosH))) / rad / 15));
  const noon = 12 - loc.lon / 15 - eq / 60 + tzHours();
  return { rise: noon - H, set: noon + H };
}

/** Local clock hours now, with minutes. */
export function liveClock(): number {
  const d = new Date();
  return d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
}

/** Map a local clock time onto the palette's day: sunrise becomes 6, sunset 18. */
export function paletteHour(clock: number): number {
  const { rise, set } = sunTimes();
  if (clock < rise) return (clock / rise) * 6;
  if (clock <= set) return 6 + ((clock - rise) / (set - rise)) * 12;
  return 18 + ((clock - set) / (24 - set)) * 6;
}

export const fmt = (h: number): string => {
  const t = ((h % 24) + 24) % 24;
  const hh = Math.floor(t), mm = Math.floor((t - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};
